import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { forgotPasswordRequest } from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { forgotPasswordSchema } from 'core/helpers/yupSchemas';

import { ReactComponent as Lock } from 'assets/icons/lock.svg';

import { ResetEmailConfirmation } from './ResetEmailConfirmation';
import { ResetError } from './ResetError';

const baseUrl = document.location.origin;

export const ResetEmailForm = () => {
	const { t: resetEmailTranslate } = useTranslation(NamespacesList['resetEmail']);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const userData = useSelector(getUser);
	const [isEmailSent, setIsEmailSent] = useState(false);
	const [isEmailWrong, setIsEmailWrong] = useState(false);
	const [email, setEmail] = useState('');

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(forgotPasswordSchema()),
	});

	const redirectToLogin = () => {
		navigate(ROUTES.LOGIN);
	};

	const resetRequest = (data: any) => {
		const { email } = data;
		setEmail(email);

		const payload = {
			email,
			redirectUrl: `${baseUrl}/reset-password/new`,
		};

		dispatch(forgotPasswordRequest(payload)).then((res: any) => {
			if (res.type === 'user/forgotPassword/rejected' || res.payload === undefined) {
				setIsEmailWrong(true);
			} else {
				setIsEmailSent(true);
			}
		});
	};

	const renderSumModal = () => {
		if (isEmailWrong) {
			return <ResetError email={email} setIsEmailWrong={setIsEmailWrong} />;
		}
		if (isEmailSent) {
			return <ResetEmailConfirmation email={email} resetRequest={resetRequest} />;
		}

		return null;
	};

	return (
		<>
			{isEmailSent || isEmailWrong ? (
				renderSumModal()
			) : (
				<Box
					className="rtl-able"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						alignSelf: 'center',
						width: { xs: '100%', sm: '438px' },
						margin: '48px 0',
						gap: '8px',
					}}
				>
					<Lock />

					<Typography
						variant="h3"
						sx={{ fontSize: '24px', fontWeight: '700', marginTop: { xs: '16px', sm: '32px' } }}
					>
						{resetEmailTranslate('text.title')}
					</Typography>

					<Typography sx={{ color: '#9FA6B3', marginTop: '8px', textAlign: 'center' }}>
						{resetEmailTranslate('text.description')}
					</Typography>

					<form style={{ width: '95%' }} onSubmit={handleSubmit(resetRequest)}>
						<FormControl fullWidth error={!!errors.email}>
							<Typography sx={{ fontSize: '12px' }}>{resetEmailTranslate('text.email')}</Typography>
							<TextField
								inputProps={{ style: { padding: '10px 12px' } }}
								placeholder="example@gmail.com"
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								{...register('email')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.email?.message}
							</FormHelperText>
						</FormControl>

						<Grid
							item
							sx={{
								display: 'flex',
								width: '100%',
								justifyContent: 'flex-start',
								gap: '8px',
								marginTop: '16px',
							}}
						>
							<Typography>{resetEmailTranslate('text.rememberPassword')}</Typography>
							<Typography sx={{ color: '#4786FF', cursor: 'pointer' }} onClick={redirectToLogin}>
								{resetEmailTranslate('text.logIn')}
							</Typography>
						</Grid>

						<Grid
							className="not-rtl-able"
							container
							direction="row"
							sx={{
								width: '100%',
								display: 'flex',
								justifyContent: 'space-between',
								marginTop: '24px',
							}}
						>
							<Button
								sx={{
									height: '44px',
									width: { xs: '45%', sm: '204px' },
									borderRadius: '8px',
									textTransform: 'none',
									fontSize: '16px',
									fontWeight: '700',
								}}
								type="submit"
								variant="outlined"
								color="primary"
								onClick={redirectToLogin}
							>
								{resetEmailTranslate('buttons.cancel')}
							</Button>

							{userData.loading ? (
								<Button
									sx={{
										height: '44px',
										width: { xs: '50%', sm: '204px' },
										borderRadius: '8px',
									}}
									variant="contained"
									color="primary"
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#91B6FF"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Button>
							) : (
								<Button
									sx={{
										height: '44px',
										width: { xs: '50%', sm: '207px' },
										borderRadius: '8px',
										textTransform: 'none',
										fontSize: '16px',
										fontWeight: '700',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									{resetEmailTranslate('buttons.submit')}
								</Button>
							)}
						</Grid>
					</form>
				</Box>
			)}
		</>
	);
};
